// components/ui/Spinner.tsx
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { motion } from 'framer-motion';

interface SpinnerProps {
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    className?: string;
    variant?: 'teal' | 'white' | 'slate' | 'emerald' | 'amber';
}

export function Spinner({ size = 'md', className, variant = 'teal' }: SpinnerProps) {
    const sizes: Record<string, { container: string; stroke: string }> = {
        xs: { container: 'h-3 w-3', stroke: '3' },
        sm: { container: 'h-5 w-5', stroke: '2.5' },
        md: { container: 'h-8 w-8', stroke: '2.5' },
        lg: { container: 'h-12 w-12', stroke: '2' },
        xl: { container: 'h-16 w-16', stroke: '2' },
    };

    const variants: Record<string, { track: string; spinner: string }> = {
        teal: { track: 'text-emerald-200', spinner: 'text-emerald-600' },
        white: { track: 'text-white/30', spinner: 'text-white' },
        slate: { track: 'text-slate-200', spinner: 'text-slate-500' },
        emerald: { track: 'text-emerald-100', spinner: 'text-emerald-500' },
        amber: { track: 'text-amber-100', spinner: 'text-amber-500' }
    };

    const s = sizes[size];
    const v = variants[variant];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={twMerge(clsx('flex items-center justify-center', className))}
        >
            <motion.svg
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className={s.container}
                viewBox="0 0 24 24"
                fill="none"
            >
                <circle
                    className={v.track}
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth={s.stroke}
                />
                <motion.path
                    className={v.spinner}
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    initial={{ opacity: 0.5 }}
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                />
            </motion.svg>
        </motion.div>
    );
}

export function PageLoader({ message = 'Loading...' }: { message?: string }) {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center min-h-[400px] gap-6"
        >
            <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            >
                <Spinner size="xl" variant="emerald" />
            </motion.div>
            <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-sm font-medium text-slate-400"
            >
                {message}
            </motion.p>
        </motion.div>
    );
}